import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Table, useModalControl } from '@weave/design-system';
import { Dispute } from '@weave/schema-gen-ts/dist/schemas/payments-platform/justifi-integration/justifi_integration.pb';
import { useInfiniteGetJustifiDisputeList } from 'apis/payments/justifi-integration/v1/queries/use-get-dispute-list';
import { Page } from 'components/shared';
import { Loader } from 'components/shared/loader/loader.component';
import { selectCurrentLocation } from 'redux/actions/location';
import { generateColumns } from './dispute-columns';
import { DisputeDetailsModal } from './dispute-details.modal';

export const JustifiDisputes = () => {
  const currentLocation = useSelector(selectCurrentLocation);
  const columns = useMemo(() => generateColumns, []);
  const {
    data,
    pageConfig,
    setPageConfig,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    loading,
    refetch,
  } = useInfiniteGetJustifiDisputeList({ locationId: currentLocation.LocationID });
  const [selectedDispute, setSelectedDispute] = useState<Dispute>();
  const { triggerProps, modalProps, closeModal } = useModalControl();

  if (loading) {
    return <Loader />;
  }

  const onCloseModal = () => {
    refetch();
    closeModal();
  };

  return (
    <Page title="JustiFi Disputes">
      <Table
        colConfig={columns}
        data={data?.pages[pageConfig.pageNumber - 1]?.disputes || []}
        emptyStateConfig={{
          type: 'payments',
          header: 'No disputes to display',
        }}
        rowActions={{
          onRowClick: (dispute) => {
            setSelectedDispute(dispute);
            triggerProps.onClick();
          },
        }}
        isPaginated
        manualPaginationConfig={{
          hasNext: hasNextPage,
          hasPrevious: hasPreviousPage,
          onNumRowsChange: (num) => {
            setPageConfig({ pageNumber: 1, pageSize: num });
          },
          page: pageConfig.pageNumber,
          defaultRowsPerPage: pageConfig.pageSize,
          rowsPerPageOptions: [10, 15, 30, 50],
          handleChange: (prevOrNext: 'next' | 'prev') => {
            if (prevOrNext === 'next') {
              setPageConfig({ ...pageConfig, pageNumber: pageConfig.pageNumber + 1 });
              fetchNextPage();
            } else {
              setPageConfig({ ...pageConfig, pageNumber: pageConfig.pageNumber - 1 });
              fetchPreviousPage();
            }
          },
        }}
      />
      <DisputeDetailsModal
        modalProps={modalProps}
        closeModal={onCloseModal}
        locationId={currentLocation?.LocationID}
        disputeId={selectedDispute?.id || ''}
      />
    </Page>
  );
};
